import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { captureError } from '@/lib/telemetry';
import { Eye, Trash2, ChevronDown, ChevronRight, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

type SenderType = 'user' | 'admin' | 'ai' | 'system';

interface TicketMessageSender {
  id: string;
  username: string | null;
  displayName: string | null;
}

interface TicketMessage {
  messageId: string;
  senderId: string | null;
  /** Resolved identity, supplied by the admin endpoint only. */
  sender?: TicketMessageSender | null;
  senderType: string;
  content: string;
  isInternal?: boolean;
  deletedAt?: string | null;
  createdAt: string;
}

interface TicketMessageListProps {
  messages: TicketMessage[];
  isAdmin?: boolean;
  /**
   * The viewing admin's user id. Lets the thread say "You" for this agent's own
   * replies while naming every other agent — without it, a second agent's reply
   * would also read "You".
   */
  currentUserId?: string;
  onDeleteMessage?: (messageId: string) => void;
  /** Omit to hide the restore action on deleted messages. */
  onRestoreMessage?: (messageId: string) => void;
}

// Sender labels are perspective-dependent: "You" always means the person
// viewing the thread. In the customer-facing view that's the ticket's `user`;
// in the admin view it's the `admin` (support agent), so the customer's own
// messages must read as "Customer", never "You".
const CUSTOMER_VIEW_LABELS: Record<SenderType, string> = {
  user: 'You',
  admin: 'Support Agent',
  ai: 'AI Assistant',
  system: 'System',
};

const ADMIN_VIEW_LABELS: Record<SenderType, string> = {
  user: 'Customer',
  admin: 'You',
  ai: 'AI Assistant',
  system: 'System',
};

/**
 * Resolve the viewer's perspective. `ownType` is the sender whose messages
 * belong to the viewer — they render on the right ("own" side); everyone
 * else renders on the left.
 */
function getPerspective(isAdmin?: boolean): {
  labels: Record<SenderType, string>;
  ownType: SenderType;
} {
  return isAdmin
    ? { labels: ADMIN_VIEW_LABELS, ownType: 'admin' }
    : { labels: CUSTOMER_VIEW_LABELS, ownType: 'user' };
}

/**
 * Role labels for an admin-view message that is NOT the viewer's own and whose
 * identity could not be resolved (DynamoDB lookup miss). Deliberately distinct
 * from ADMIN_VIEW_LABELS, whose `admin` entry is "You" — falling back to that
 * would attribute a colleague's reply to the person reading it.
 */
const UNRESOLVED_SENDER_LABELS: Record<SenderType, string> = {
  user: 'Customer',
  admin: 'Support Agent',
  ai: 'AI Assistant',
  system: 'System',
};

/**
 * The name shown above a bubble.
 *
 * Only the admin view names people: it resolves each sender to their display
 * name so a multi-agent thread shows who actually replied, and reserves "You"
 * for the viewing agent's own messages — matched on sender id, never on the
 * `admin` role, which every agent shares. The customer-facing view keeps the
 * generic role labels; agent identities never cross that boundary.
 */
function senderLabel(
  message: TicketMessage,
  senderKey: SenderType,
  labels: Record<SenderType, string>,
  isAdmin?: boolean,
  currentUserId?: string,
): string {
  if (!isAdmin || senderKey === 'ai' || senderKey === 'system') return labels[senderKey];
  if (currentUserId && message.sender?.id === currentUserId) return labels.admin; // "You"
  return (
    message.sender?.displayName || message.sender?.username || UNRESOLVED_SENDER_LABELS[senderKey]
  );
}

/** Bubble colour by sender, with the viewer's own messages using the accent. */
function bubbleClass(senderKey: SenderType, isOwn: boolean): string {
  if (senderKey === 'ai') {
    return 'bg-violet-100 text-violet-900 dark:bg-violet-900 dark:text-violet-100';
  }
  return isOwn ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground';
}

function DeletedMessageContent({
  message,
  isAdmin,
  currentUserId,
  onRestoreMessage,
}: {
  message: TicketMessage;
  isAdmin?: boolean;
  currentUserId?: string;
  onRestoreMessage?: (messageId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const handleRestore = async () => {
    if (!onRestoreMessage || restoring) return;
    setRestoring(true);
    try {
      await onRestoreMessage(message.messageId);
    } catch (err) {
      // Swallow the rejection so it doesn't surface as an unhandled promise
      // rejection (the onClick handler can't catch it). The parent owns any
      // user-facing error surface; here we just record it for diagnosis.
      captureError(err instanceof Error ? err : new Error(String(err)), {
        context: 'customer-support:restore-message',
        messageId: message.messageId,
      });
    } finally {
      // The row disappears from the deleted state on success; on failure the
      // button must become clickable again rather than staying stuck.
      setRestoring(false);
    }
  };

  const { labels, ownType } = getPerspective(isAdmin);
  const senderKey = (message.senderType in labels ? message.senderType : 'system') as SenderType;
  const label = senderLabel(message, senderKey, labels, isAdmin, currentUserId);
  const isOwn = senderKey === ownType;
  const isInternalNote = message.isInternal === true;

  const timestamp = new Date(message.createdAt).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const dateStr = new Date(message.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

  return (
    <div
      className={cn(
        'flex flex-col gap-1',
        isOwn ? 'items-end' : 'items-start',
        isInternalNote && 'border-l-2 border-amber-400 pl-3',
      )}
    >
      <div className="flex items-center gap-2 px-1">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {isInternalNote && (
          <span className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
            <Eye className="h-3 w-3" />
            Internal Note
          </span>
        )}
        <span className="text-xs text-muted-foreground/60">
          {dateStr} {timestamp}
        </span>
        {onRestoreMessage && (
          <Button
            variant="ghost"
            size="sm"
            className="h-5 gap-1 px-1.5 text-xs text-muted-foreground hover:text-foreground"
            onClick={handleRestore}
            disabled={restoring}
            title="Restore this message to the thread"
          >
            <Undo2 className="h-3 w-3" />
            {restoring ? 'Restoring…' : 'Restore'}
          </Button>
        )}
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'max-w-[75%] rounded-lg px-3 py-2 text-sm text-left cursor-pointer',
          'bg-muted/50 text-muted-foreground border border-dashed border-muted-foreground/30',
          'hover:bg-muted/80 transition-colors',
        )}
      >
        <span className="flex items-center gap-1.5 italic">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          )}
          Message was deleted
        </span>
        {expanded && (
          <div className="mt-2 pt-2 border-t border-muted-foreground/20 whitespace-pre-wrap break-words opacity-60">
            {message.content}
          </div>
        )}
      </button>
    </div>
  );
}

export function TicketMessageList({
  messages,
  isAdmin,
  currentUserId,
  onDeleteMessage,
  onRestoreMessage,
}: TicketMessageListProps) {
  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        No messages yet.
      </div>
    );
  }

  const { labels, ownType } = getPerspective(isAdmin);

  return (
    <div className="flex flex-col gap-3 p-4">
      {messages.map((message) => {
        const isDeleted = !!message.deletedAt;

        // Admin sees deleted messages with expand toggle; user never sees them
        if (isDeleted) {
          if (!isAdmin) return null;
          return (
            <DeletedMessageContent
              key={message.messageId}
              message={message}
              isAdmin={isAdmin}
              currentUserId={currentUserId}
              onRestoreMessage={onRestoreMessage}
            />
          );
        }

        const senderKey = (
          message.senderType in labels ? message.senderType : 'system'
        ) as SenderType;
        const label = senderLabel(message, senderKey, labels, isAdmin, currentUserId);
        // `isOwn` = the viewer's own side (right-aligned, accent bubble).
        // `isUser` stays tied to the actual sender so customer messages render
        // as plain text (no markdown) regardless of who's viewing.
        const isOwn = senderKey === ownType;
        const isUser = senderKey === 'user';
        const isSystem = senderKey === 'system';

        const timestamp = new Date(message.createdAt).toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit',
        });

        const dateStr = new Date(message.createdAt).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        });

        if (isSystem) {
          return (
            <div key={message.messageId} className="flex justify-center">
              <div
                className={cn(
                  'max-w-md rounded-md px-3 py-1.5 text-xs text-center',
                  'bg-transparent text-muted-foreground border border-border',
                )}
              >
                {message.content}
                <span className="ml-2 opacity-60">
                  {dateStr} {timestamp}
                </span>
              </div>
            </div>
          );
        }

        const isInternalNote = message.isInternal === true;

        return (
          <div
            key={message.messageId}
            className={cn(
              'group flex flex-col gap-1',
              isOwn ? 'items-end' : 'items-start',
              isInternalNote && 'border-l-2 border-amber-400 pl-3',
            )}
          >
            <div className="flex items-center gap-2 px-1">
              <span className="text-xs font-medium text-muted-foreground">{label}</span>
              {isInternalNote && (
                <span className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                  <Eye className="h-3 w-3" />
                  Internal Note
                </span>
              )}
              <span className="text-xs text-muted-foreground/60">
                {dateStr} {timestamp}
              </span>
              {isAdmin && onDeleteMessage && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                  onClick={() => onDeleteMessage(message.messageId)}
                  title="Unsend message"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
            <div
              className={cn(
                'max-w-[75%] rounded-lg px-3 py-2 text-sm break-words',
                isUser && 'whitespace-pre-wrap',
                isInternalNote
                  ? 'bg-amber-50 text-amber-900 border border-amber-200 dark:bg-amber-950 dark:text-amber-100 dark:border-amber-800'
                  : bubbleClass(senderKey, isOwn),
              )}
            >
              {isUser ? (
                message.content
              ) : (
                <div className="prose prose-sm dark:prose-invert max-w-none break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
